import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { ExecutionResult, print } from "graphql";
import { useOIDCStore } from "shared/stores/oidc";
import { HttpStatusCode } from "shared/utils/http";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";
import { match, P } from "ts-pattern";

const http = async <T>(path: string, { headers, ...restConfig }: RequestInit): Promise<T> => {
    const requestUrl = `${import.meta.env.VITE_API_BASE_URL}${path}`;
    const request = new Request(requestUrl, {
        ...restConfig,
        headers: {
            ...headers,
            ...match(import.meta.env["VITE_CUSTOMER_ID_OVERRIDE"])
                .with(P.string, (id) => ({ "customer-id": id }))
                .otherwise(() => ({})),
            ...match(import.meta.env["VITE_USER_ID_OVERRIDE"])
                .with(P.nullish, () =>
                    maybe(useOIDCStore.getState().getAccessToken())?.take((token) => ({
                        Authorization: "Bearer " + token,
                    })),
                )
                .otherwise((it) => ({ ["user-id"]: it })),
        },
    });

    const response = await fetch(request).catch((error) => {
        throw new Error(`HTTP Error! Request ${JSON.stringify({ restConfig })} failed with ${error.status} and ${error.statusText}`);
    });

    if (!response.ok) {
        if (response.status == HttpStatusCode.UNAUTHORIZED) {
            useOIDCStore.getState().clearAccessToken();
            window.location.href = `/login?redirect=${encodeURI(window.location.href)}`;
        }

        throw new Error(`HTTP Error! Status: ${response.status}`);
    }

    if (response.status === HttpStatusCode.NO_CONTENT) {
        return Promise.resolve({} as T);
    }

    return response.json().catch(logger.error);
};

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

class HttpError extends Error {
    constructor(
        readonly status: number,
        readonly statusText: string,
        readonly url: string,
    ) {
        super(`HTTP ${status} ${statusText} (${url})`);
    }
}

type Handling<T> = (req: Request, res: Response) => T | Promise<T>;

async function handleAsJSON<T>(req: Request, res: Response): Promise<T> {
    if (res.status === 201) return {} as T;
    try {
        return (await res.json()) as T;
    } catch (e) {
        logger.error(e);
        throw new Error(`Failed to parse JSON from ${req.url}`);
    }
}

export const createApiRequest = async <T>(path: string, init: RequestInit = {}, handling: Handling<T> = handleAsJSON): Promise<T> => {
    const req = new Request(`${BASE_URL}${path}`, { ...init, headers: { ...init.headers } });

    const res = await fetch(req).catch((err: Error) => {
        throw new Error(`Network error for ${req.method} ${req.url}: ${String(err?.message ?? err)}`);
    });

    if (!res.ok) {
        if (res.status === 401) {
            window.location.href = "/logout";
        }

        throw new HttpError(res.status, res.statusText, req.url);
    }

    return await Promise.resolve(handling(req, res));
};

export const httpPostFormData = async <T extends FormData, U>(path: string, body: T, config?: RequestInit): Promise<U> => {
    const init = { method: "POST", body, ...config };

    return await http<U>(path, init);
};

export const httpGet = async <T>(path: string, config?: RequestInit): Promise<T> => {
    const init = { method: "GET", headers: { "Content-Type": "application/json" }, ...config };

    return await http<T>(path, init);
};

export const httpDelete = async <T>(path: string, config?: RequestInit): Promise<T> => {
    const init = { method: "DELETE", ...config };

    return await http<T>(path, init);
};

/**
 * Drops the `| null` stupidity from the optional graphql types... `\_(-_-)_/`
 */

export type StrictUndef<T> = T extends (infer U)[]
    ? StrictUndef<NonNullable<U>>[]
    : T extends object
      ? { [K in keyof T]: StrictUndef<NonNullable<T[K]>> }
      : Exclude<T, null>;

export const httpPostGraphql = async <T, V>(
    document: TypedDocumentNode<T, V>,
    variables: V = {} as V,
    config?: RequestInit,
): Promise<NonNullable<StrictUndef<ExecutionResult<T>["data"]>>> => {
    const body = {
        query: print(document),
        variables,
    };
    const init = {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        ...config,
    };

    const response = await http<ExecutionResult<T>>("/graphql", init);

    if (response.errors) {
        throw new Error("[Graphql Error]: ".concat(response.errors.map((err) => err.message).join(", ")));
    }

    if (!response.data) {
        throw new Error("[Graphql Error]: No data");
    }

    return response.data as StrictUndef<typeof response.data>;
};
